import * as React from "react";
import { Component } from "react";
import { Button, Input, Modal, ModalFooter, ModalHeader } from "reactstrap";
import ModalBody from "reactstrap/lib/ModalBody";
import Autocomplete from "../autocomplete/Autocomplete";
import i18n from "i18next";

type ParamMeta = { description: string; default?: string };

const TASK_PARAMS: Record<string, Record<string, ParamMeta>> = {
  IMPORT_DICOM: {
    dirPath: { description: "Absolute path to the directory containing files" },
    format: { description: "Import format: gdata or g3d", default: "g3d" },
  },
  EVAL_GROUND_TRUTH_TASK: {
    evalCode: { description: "Evaluation code" },
  },
  EVAL_RECONCILE_TASK: {
    evalCode: { description: "Evaluation code" },
  },
  EVAL_RESULT_TASK: {
    evalCode: { description: "Evaluation code" },
  },
};

type TaskOptions = {
  type: string;
  params: Object;
};

interface ParamRow {
  key: string;
  value: string;
}

interface TaskModalProps {
  taskTypes?: string[];

  onLaunch(taskOptions: TaskOptions): void;

  onCancel(): void;
}

interface TaskModalState {
  type?: string;
  rows: ParamRow[];
}

export default class TaskModal extends Component<TaskModalProps, TaskModalState> {
  constructor(props: TaskModalProps) {
    super(props);

    this.state = {
      type: "",
      rows: [{ key: "", value: "" }],
    };
  }

  private onTaskTypeSelected(type: string) {
    const knownParams = TASK_PARAMS[type];
    const rows: ParamRow[] = [];
    if (knownParams) {
      Object.entries(knownParams).forEach(([key, meta]) => {
        rows.push({ key, value: meta.default || "" });
      });
    }
    rows.push({ key: "", value: "" });
    this.setState({ type, rows });
  }

  private onRowKeyChanged(index: number, key: string) {
    const rows = [...this.state.rows];
    rows[index] = { ...rows[index], key };
    if (index === rows.length - 1 && key !== "") {
      rows.push({ key: "", value: "" });
    }
    this.setState({ rows });
  }

  private onRowValueChanged(index: number, value: string) {
    const rows = [...this.state.rows];
    rows[index] = { ...rows[index], value };
    this.setState({ rows });
  }

  private buildParams(): Record<string, string> {
    const params: Record<string, string> = {};
    this.state.rows.forEach((row) => {
      if (row.key.trim() !== "") {
        params[row.key.trim()] = row.value;
      }
    });
    return params;
  }

  private getValuePlaceholder(key: string): string {
    const knownParams = this.state.type ? TASK_PARAMS[this.state.type] : undefined;
    return knownParams && knownParams[key] ? knownParams[key].description : "value";
  }

  private onCancel() {
    this.props.onCancel();
  }

  private onLaunch() {
    this.props.onLaunch({ type: this.state.type, params: this.buildParams() });
  }

  private onResetTaskParams() {
    this.onTaskTypeSelected(this.state.type || "");
  }

  render() {
    return (
      <Modal isOpen={true}>
        <ModalHeader>Launch task</ModalHeader>
        <ModalBody>
          <Autocomplete
            items={this.props.taskTypes}
            placeholder={i18n.t("task.taskType")}
            value={this.state.type}
            onChange={(type) => this.onTaskTypeSelected(type)}
            toLabel={(type) => type}
            renderItem={(type) => <span key={type}>{type}</span>}
          />

          <div className="container mt-2">
            {this.state.rows.map((row, index) => (
              <div className="row mb-1" key={index}>
                <Input
                  value={row.key}
                  type="text"
                  placeholder="key"
                  className="col-sm-5"
                  onChange={(evt) => this.onRowKeyChanged(index, evt.target.value)}
                />
                <Input
                  value={row.value}
                  type="text"
                  placeholder={this.getValuePlaceholder(row.key)}
                  className="col-sm-7"
                  onChange={(evt) => this.onRowValueChanged(index, evt.target.value)}
                />
              </div>
            ))}

            <div className="row pt-2 border-secondary">
              <div className="col-sm-10">
                <small className="text-muted">params : {JSON.stringify(this.buildParams())}</small>
              </div>
              <Button className="col-sm-2" size="sm" onClick={() => this.onResetTaskParams()}>
                <i className="fa fa-undo" />
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => this.onLaunch()}>
            {i18n.t("task.launch")}
          </Button>{" "}
          <Button color="secondary" onClick={() => this.onCancel()}>
            {i18n.t("task.cancel")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}